import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar,
  Clock, 
  MapPin,
  Users,
  Video,
  Building,
  CheckCircle2,
  X,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-[#1E3A5F] font-medium">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function PublicSchedulePage() {
  const [searchParams] = useSearchParams();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    license_number: '',
    address: '',
    city: '',
    state: 'TX',
    zip_code: '',
    how_heard: ''
  });

  useEffect(() => {
    fetchSchedule();
  }, []);

  useEffect(() => {
    const registerId = searchParams.get('register');
    if (registerId && schedule.length > 0) {
      const session = schedule.find(s => s.id === registerId);
      if (session) {
        setSelectedSession(session);
        setShowRegisterDialog(true);
      }
    }
  }, [searchParams, schedule]);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/schedule`);
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.license_number.trim()) {
      toast.error('License number is required for TREC CE credit');
      return;
    }
    
    setRegistering(true);
    
    try {
      await axios.post(`${API_URL}/public/register`, {
        ...formData,
        scheduled_course_id: selectedSession.id
      });
      
      setRegistered(true);
      toast.success('Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const closeDialog = () => {
    setShowRegisterDialog(false);
    setSelectedSession(null);
    setRegistered(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      license_number: '',
      address: '',
      city: '',
      state: 'TX',
      zip_code: '',
      how_heard: ''
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Group schedule by month
  const groupedSchedule = schedule.reduce((acc, session) => {
    const month = new Date(session.scheduled_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(session);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...PageSEO.schedule} />
      <PublicNav />
      
      {/* Header */}
      <section className="bg-[#1E3A5F] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Upcoming Classes
          </h1>
          <p className="text-sky-200 text-lg">
            Register for scheduled webinars and in-person CE courses
          </p>
        </div>
      </section>
      
      {/* Schedule List */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-500 mt-4">Loading schedule...</p>
            </div>
          ) : schedule.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Upcoming Classes</h3>
                <p className="text-slate-500 mb-6">
                  Check back soon for new scheduled courses, or request a private session for your team.
                </p>
                <Link to="/public/request">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                    Request a Course
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            Object.entries(groupedSchedule).map(([month, sessions]) => (
              <div key={month} className="mb-8">
                <h2 className="text-xl font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {month}
                </h2>
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <Card key={session.id} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="py-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div 
                              className="w-16 h-16 rounded-lg flex flex-col items-center justify-center text-white shrink-0"
                              style={{ backgroundColor: session.color || '#1E3A5F' }}
                            >
                              <span className="text-2xl font-bold">
                                {new Date(session.scheduled_date).getDate()}
                              </span>
                              <span className="text-xs uppercase">
                                {new Date(session.scheduled_date).toLocaleDateString('en-US', { month: 'short' })}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-[#1E3A5F] text-lg">{session.course_title}</h3>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 mt-2">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {session.scheduled_time} • {session.credit_hours} CE Hour{session.credit_hours > 1 ? 's' : ''}
                                </span>
                                <span className="flex items-center gap-1">
                                  {session.delivery_format === 'webinar' ? (
                                    <><Video className="w-4 h-4" /> Webinar</>
                                  ) : (
                                    <><Building className="w-4 h-4" /> In-Person</>
                                  )}
                                </span>
                                {session.instructor_name && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {session.instructor_name}
                                  </span>
                                )}
                              </div>
                              {session.location && (
                                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {session.location}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button 
                            className="bg-sky-500 hover:bg-sky-600 text-white shrink-0"
                            onClick={() => {
                              setSelectedSession(session);
                              setShowRegisterDialog(true);
                            }}
                          >
                            Register Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      
      {/* Registration Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {registered ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">Registration Confirmed!</h2>
              <p className="text-slate-600 mb-6">
                You're registered for <strong>{selectedSession?.course_title}</strong> on{' '}
                {selectedSession && formatDate(selectedSession.scheduled_date)}.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                A confirmation email will be sent to {formData.email} with details and instructions.
              </p>
              <Button onClick={closeDialog} className="bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white">
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                  Register for Course
                </DialogTitle>
              </DialogHeader>
              
              {selectedSession && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-[#1E3A5F]">{selectedSession.course_title}</h4>
                  <p className="text-sm text-slate-600">
                    {formatDate(selectedSession.scheduled_date)} at {selectedSession.scheduled_time}
                  </p>
                  <p className="text-sm text-slate-500">
                    {selectedSession.credit_hours} CE Hour{selectedSession.credit_hours > 1 ? 's' : ''} • 
                    {selectedSession.delivery_format === 'webinar' ? ' Webinar' : ' In-Person'}
                  </p>
                </div>
              )}
              
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Company/Brokerage</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>TREC License Number *</Label>
                  <Input
                    required
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    placeholder="Required for CE credit submission"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required for TREC CE credit reporting</p>
                </div>
                
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>How did you hear about us?</Label>
                  <Select
                    value={formData.how_heard}
                    onValueChange={(v) => setFormData({ ...formData, how_heard: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="search">Online Search</SelectItem>
                      <SelectItem value="brokerage">My Brokerage</SelectItem>
                      <SelectItem value="event">Event/Conference</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={registering}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white"
                >
                  {registering ? 'Registering...' : 'Complete Registration'}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* CTA */}
      <section className="bg-[#1E3A5F] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Don't see a date that works?
          </h2>
          <p className="text-sky-200 mb-6">
            Request a private session for your brokerage or team with your preferred dates.
          </p>
          <Link to="/public/request">
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
              Request Custom Scheduling
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
